/**
 * Routes agent invocations by hiring region: EU → eu-west-1, else us-west-2.
 *
 * Source by stage:
 * - DEV: per-developer ADA runtime ARNs discovered by `bb init-ada-resources`
 *   (see `ADA_RESOURCES`). Baked into the bundle at build time.
 * - BETA/GAMMA/PROD: `AGENTCORE_CONFIG` committed consts below.
 * Returns undefined when no ARN is configured → Electron uses its own fallback.
 */

import {
  type AdaResources,
  ADA_RESOURCES,
} from "@amzn/client-workbench-test-utils/dist/lib/ada-resources";

import { type STAGES, STAGE } from "../stageConfig";

/** Typed view of the ADA Bedrock ARNs; the dependency surfaces these loosely. */
const adaBedrock: AdaResources["BedrockAgentCore"] | undefined =
  ADA_RESOURCES.BedrockAgentCore;

type AgentCoreRegionConfig = {
  readonly region: string;
  readonly runtimeArn: string;
};

type AgentCoreStageConfig = {
  readonly US: AgentCoreRegionConfig;
  readonly EU: AgentCoreRegionConfig;
};

/** AgentCore Runtime ARNs per stage and region. DEV comes from the developer's ADA. */
export const AGENTCORE_CONFIG: Partial<Record<STAGES, AgentCoreStageConfig>> = {
  DEV: {
    US: {
      region: "us-west-2",
      runtimeArn: adaBedrock?.EZO_VPN_RUNTIME_ARN ?? "",
    },
    EU: {
      region: "eu-west-1",
      runtimeArn: adaBedrock?.EZO_VPN_RUNTIME_ARN_EU ?? "",
    },
  },
  BETA: {
    US: {
      region: "us-west-2",
      runtimeArn:
        "arn:aws:bedrock-agentcore:us-west-2:878084162945:runtime/ezo_agent-ThMhj5ERgk",
    },
    EU: {
      region: "eu-west-1",
      runtimeArn:
        "arn:aws:bedrock-agentcore:eu-west-1:878084162945:runtime/ezo_agent-mA6DGvGInP",
    },
  },
  GAMMA: {
    US: {
      region: "us-west-2",
      runtimeArn:
        "arn:aws:bedrock-agentcore:us-west-2:106143622186:runtime/ezo_agent-RhQz1yFGF5",
    },
    EU: {
      region: "eu-west-1",
      runtimeArn:
        "arn:aws:bedrock-agentcore:eu-west-1:106143622186:runtime/ezo_agent-Pk6vbyCB5A",
    },
  },
  PROD: {
    US: {
      region: "us-west-2",
      runtimeArn:
        "arn:aws:bedrock-agentcore:us-west-2:666185136605:runtime/ezo_agent-sHHrG97D6f",
    },
    EU: {
      region: "eu-west-1",
      runtimeArn:
        "arn:aws:bedrock-agentcore:eu-west-1:666185136605:runtime/ezo_agent-4IN5tc6nyE",
    },
  },
};

/** Build the AgentCore REST API invocation URL. */
const buildInvocationUrl = (config: AgentCoreRegionConfig): string => {
  const encodedArn = encodeURIComponent(config.runtimeArn);
  return `https://bedrock-agentcore.${config.region}.amazonaws.com/runtimes/${encodedArn}/invocations?qualifier=DEFAULT`;
};

/**
 * Get the AgentCore invocation URL for the user's hiring region.
 *
 * @param hiringRegion - e.g. "EU", "USA", "KOR", ""
 * @returns URL, or undefined when the stage/region has no ARN configured.
 */
export const getAgentCoreUrl = (hiringRegion: string): string | undefined => {
  const isEU = hiringRegion === "EU";
  const stageConfig = AGENTCORE_CONFIG[STAGE];
  if (!stageConfig) {
    return undefined;
  }

  const regionConfig = isEU ? stageConfig.EU : stageConfig.US;
  if (!regionConfig.runtimeArn) {
    return undefined;
  }
  return buildInvocationUrl(regionConfig);
};
